import PromotionAnalytics from './PromotionAnalytics';

/**
 * Singleton Analytics Manager
 * Provides a shared instance of PromotionAnalytics across the application
 * and addresses performance issues of creating multiple `PromotionAnalytics`
 * instances across many promotion cards on the page.
 */
class SingletonAnalyticsManager {
  constructor() {
    this.promotionAnalytics = null;
    this.isInitializing = false;
    this.initPromise = null;
  }

  /**
   * Get the singleton instance of PromotionAnalytics
   * @returns {Promise<PromotionAnalytics>}
   */
  async getInstance() {
    if (this.promotionAnalytics && this.promotionAnalytics.isReady()) {
      return this.promotionAnalytics;
    }

    // If already initializing, return the existing promise
    if (this.isInitializing && this.initPromise) {
      return this.initPromise;
    }

    // Start initialization
    this.isInitializing = true;
    this.initPromise = this.initializeInstance();

    try {
      await this.initPromise;
      return this.promotionAnalytics;
    } catch (error) {
      // Reset state on error so next call can retry
      this.isInitializing = false;
      this.initPromise = null;
      throw error;
    }
  }

  /**
   * Initialize the PromotionAnalytics instance
   * @returns {Promise<PromotionAnalytics>}
   * @private
   */
  async initializeInstance() {
    if (!this.promotionAnalytics) {
      this.promotionAnalytics = new PromotionAnalytics();
    }

    const initialized = await this.promotionAnalytics.initialize();

    if (!initialized) {
      console.warn('Failed to initialize singleton PromotionAnalytics');
      // Don't throw error, just return the instance so components can handle gracefully
    }

    this.isInitializing = false;
    console.debug('Singleton PromotionAnalytics initialized');

    return this.promotionAnalytics;
  }

  /**
   * Check if the analytics instance is ready
   * @returns {boolean}
   */
  isReady() {
    return this.promotionAnalytics && this.promotionAnalytics.isReady();
  }

  /**
   * Force reset the singleton (useful for testing)
   */
  reset() {
    this.promotionAnalytics = null;
    this.isInitializing = false;
    this.initPromise = null;
  }
}

const analyticsManager = new SingletonAnalyticsManager();

/**
 * Get the shared PromotionAnalytics instance
 * @returns {Promise<PromotionAnalytics>}
 */
export async function getPromotionAnalytics() {
  return analyticsManager.getInstance();
}

/**
 * Check if promotion analytics is ready without initializing
 * @returns {boolean}
 */
export function promotionAnalyticsIsReady() {
  return analyticsManager.isReady();
}

/**
 * Reset the singleton (primarily for testing)
 */
export function resetPromotionAnalytics() {
  analyticsManager.reset();
}

export default analyticsManager;
